import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { MdClose } from '@teamworksdev/icons';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { classifyMoveDestinations } from '../sub-track-move';
import type { TrackPreview } from '../types';

export interface MoveSubTrackModalProps {
  open: boolean;
  onClose: () => void;
  /** The sub-track being moved. */
  subTrack: TrackPreview;
  /** The sub-track's current parent — used in the roster-callout copy. */
  currentParentTrack: TrackPreview;
  /** Pre-filled destination for the drag entry; undefined for the context-menu entry. */
  destinationTrack?: TrackPreview;
  /** All tracks on the team — source for the destination picker options. */
  allTracks: TrackPreview[];
  /**
   * Called with the chosen destination when the coach commits to the move.
   * The modal closes immediately after calling this — it does not wait on the
   * mutation. The caller owns firing it, and reporting success or failure
   * (by toast), since there is no modal left open to show either in.
   */
  onConfirm: (destinationTrack: TrackPreview) => void;
}

/**
 * The Move Sub-Track modal.
 *
 * One modal for both entry points. The cross-parent drag already knows the
 * destination, so it renders the full PRD copy (title + roster callout) with
 * Move enabled; the context menu knows nothing until the coach picks a
 * destination, so it opens on a generic title and reveals the same copy once a
 * destination is chosen.
 *
 * Move closes the modal immediately and hands the chosen destination to
 * `onConfirm` — the caller fires the mutation and reports success or failure
 * by toast, same as the sibling `MoveTrackModal`. The coach already committed
 * to a destination by clicking Move; a rejection (e.g. a same-name sibling
 * landing there moments earlier) is something to be told, not a prompt to
 * pick a different one from inside a dead-ended modal.
 */
export function MoveSubTrackModal({
  open,
  onClose,
  subTrack,
  currentParentTrack,
  destinationTrack,
  allTracks,
  onConfirm,
}: MoveSubTrackModalProps) {
  const { t } = useTranslation();

  const [selectedDestinationId, setSelectedDestinationId] = useState(
    destinationTrack?.id ?? '',
  );

  const { eligible: destinations, blocked: unavailableDestinations } = useMemo(
    () => classifyMoveDestinations(subTrack, currentParentTrack.id, allTracks),
    [subTrack, currentParentTrack.id, allTracks],
  );

  // The caller (TimelinePage) checks eligibility before ever opening the
  // modal on a drag destination, so it is trusted as-is here rather than
  // re-filtered through `destinations`. If it goes stale while the modal is
  // already open (e.g. a same-name sibling synced in elsewhere mid-move),
  // Move still submits it and the rejection is reported by toast — the same
  // path any other late rejection takes. Re-validating here instead would
  // swap to the picker with no explanation for a drop the coach already
  // made, which is the exact confusion the drag path was fixed to avoid.
  const selectedDestination =
    destinationTrack ??
    destinations.find((track) => track.id === selectedDestinationId);

  // Each open starts from what its entry point knows: the drag passes the
  // destination, the context menu passes nothing.
  useEffect(() => {
    if (!open) return;
    setSelectedDestinationId(destinationTrack?.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const titleId = 'move-sub-track-title';
  const canMove = !!selectedDestination;

  const handleMove = () => {
    if (!selectedDestination) return;
    onConfirm(selectedDestination);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        id={titleId}
        className="flex items-center justify-between p-6"
      >
        {selectedDestination
          ? t('moveSubTrackTitle', {
              subTrack: subTrack.name,
              destination: selectedDestination.name,
            })
          : t('moveSubTrackGenericTitle', { subTrack: subTrack.name })}
        <IconButton onClick={onClose} aria-label={t('close')}>
          <MdClose size={24} />
        </IconButton>
      </DialogTitle>

      <DialogContent className="flex flex-col gap-4">
        {/* The drag entry already chose the destination; only the context-menu
            entry needs a picker. */}
        {!destinationTrack && (
          <TextField
            select
            fullWidth
            label={t('moveSubTrackDestination')}
            id="move-sub-track-destination"
            value={selectedDestinationId}
            onChange={(e) => setSelectedDestinationId(e.target.value)}
            SelectProps={{
              displayEmpty: true,
              // The theme drops the outline notch, but Select re-adds it for
              // `displayEmpty`; suppressing it keeps the border unbroken while
              // the label floats above it like every other field.
              notched: false,
              renderValue: (selected) =>
                destinations.find((track) => track.id === selected)?.name ?? (
                  <Typography component="span" sx={{ color: 'text.secondary' }}>
                    {t('moveSubTrackDestinationPlaceholder')}
                  </Typography>
                ),
            }}
          >
            {destinations.map((track) => (
              <MenuItem key={track.id} value={track.id}>
                {track.name}
              </MenuItem>
            ))}

            {/* Collisions stay in the list, disabled and labelled, so a coach
                  looking for a parent they expect to see learns why it is not
                  offered rather than finding a gap. */}
            {unavailableDestinations.map(({ track, reason }) => (
              <MenuItem
                key={track.id}
                value={track.id}
                disabled
                // MUI dims a disabled item to 38%, which would obscure the
                // reason it is there to show. The nested selector is needed
                // to beat the specificity of MUI's own disabled rule; the
                // muted text colour keeps it reading as unavailable.
                sx={{
                  '&.Mui-disabled': { opacity: 1 },
                  color: 'text.secondary',
                }}
              >
                {track.name}
                {reason === 'nameCollision' && (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontStyle: 'italic',
                      ml: 1,
                    }}
                  >
                    {'('}
                    <Trans
                      i18nKey="moveSubTrackDestinationNameCollision"
                      values={{ subTrack: subTrack.name }}
                    />
                    {')'}
                  </Typography>
                )}
              </MenuItem>
            ))}
          </TextField>
        )}

        {selectedDestination && (
          <Alert severity="info" role="status">
            {t('moveSubTrackRosterMessage', {
              subTrack: subTrack.name,
              destination: selectedDestination.name,
              currentParent: currentParentTrack.name,
            })}
          </Alert>
        )}
      </DialogContent>

      <DialogActions className="p-6">
        <Button variant="outlined" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button variant="contained" onClick={handleMove} disabled={!canMove}>
          {t('moveSubTrack')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
